import { useLayoutEffect, type RefObject } from "react";

type GsapApi = typeof import("gsap").gsap;
const REVEAL_MOTION_PENDING_CLASS = "reveal-motion-pending";

export function createRetryableAsyncLoader<T>(
  importer: () => Promise<T>,
): () => Promise<T | null> {
  let pending: Promise<T | null> | null = null;

  return () => {
    pending ??= importer().catch(() => {
      pending = null;
      return null;
    });
    return pending;
  };
}

export function shouldUseGsapMotion(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return true;
  return window.matchMedia(
    "(prefers-reduced-motion: no-preference)",
  ).matches;
}

const importGsap = createRetryableAsyncLoader<GsapApi>(async () => {
  const [gsapModule, scrollTriggerModule] = await Promise.all([
    import("gsap"),
    import("gsap/ScrollTrigger"),
  ]);
  const { gsap } = gsapModule;
  gsap.registerPlugin(scrollTriggerModule.ScrollTrigger);
  return gsap;
});

export function loadGsap(): Promise<GsapApi | null> {
  if (!shouldUseGsapMotion()) return Promise.resolve(null);
  return importGsap();
}

export function useRevealMotion<T extends HTMLElement>(
  rootRef: RefObject<T | null>,
) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let cancelled = false;
    let disposeMotion = () => {};
    const motionEnabled = shouldUseGsapMotion();

    if (!motionEnabled) return;
    root.classList.add(REVEAL_MOTION_PENDING_CLASS);

    void loadGsap()
      .then((gsap) => {
        if (cancelled || !gsap) return;

        const media = gsap.matchMedia();
        const context = gsap.context(() => {
          media.add("(prefers-reduced-motion: no-preference)", () => {
            gsap.utils
              .toArray<HTMLElement>(".reveal:not(.world-panel)")
              .forEach((element) => {
                gsap.from(element, {
                  y: 34,
                  opacity: 0,
                  immediateRender: false,
                  duration: 0.72,
                  ease: "power3.out",
                  scrollTrigger: {
                    trigger: element,
                    start: "top 88%",
                    once: true,
                  },
                });
              });

            gsap.utils.toArray<HTMLElement>(".world-panel").forEach((panel) => {
              const mediaElement =
                panel.querySelector<HTMLElement>(".world-panel__media");
              const copyElement =
                panel.querySelector<HTMLElement>(".world-panel__copy");
              const timeline = gsap.timeline({
                scrollTrigger: {
                  trigger: panel,
                  start: "top 82%",
                  once: true,
                },
              });

              timeline
                .from(panel, {
                  clipPath: "inset(12% 7% 12% 7% round 54px)",
                  scale: 0.94,
                  immediateRender: false,
                  duration: 0.95,
                  ease: "power3.out",
                })
                .from(
                  mediaElement,
                  {
                    scale: 1.16,
                    immediateRender: false,
                    duration: 1.15,
                    ease: "power2.out",
                  },
                  0,
                )
                .from(
                  copyElement,
                  {
                    y: 42,
                    opacity: 0,
                    immediateRender: false,
                    duration: 0.72,
                    ease: "power3.out",
                  },
                  0.18,
                );
            });
          });

          media.add(
            "(min-width: 900px) and (prefers-reduced-motion: no-preference)",
            () => {
              gsap.utils
                .toArray<HTMLElement>(".world-panel__media")
                .forEach((mediaElement) => {
                  gsap.fromTo(
                    mediaElement,
                    { yPercent: -5 },
                    {
                      yPercent: 7,
                      ease: "none",
                      scrollTrigger: {
                        trigger: mediaElement.closest(".visual-world"),
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 0.8,
                      },
                    },
                  );
                });

              gsap.utils
                .toArray<HTMLElement>(".world-orb")
                .forEach((orb, index) => {
                  gsap.to(orb, {
                    yPercent: index % 2 === 0 ? -90 : 70,
                    xPercent: index % 2 === 0 ? 24 : -18,
                    rotation: index % 2 === 0 ? 60 : -42,
                    ease: "none",
                    scrollTrigger: {
                      trigger: orb.closest(".visual-world"),
                      start: "top bottom",
                      end: "bottom top",
                      scrub: 1,
                    },
                  });
                });
            },
          );
        }, root);

        disposeMotion = () => {
          media.revert();
          context.revert();
        };
      })
      .finally(() => {
        root.classList.remove(REVEAL_MOTION_PENDING_CLASS);
      });

    return () => {
      cancelled = true;
      root.classList.remove(REVEAL_MOTION_PENDING_CLASS);
      disposeMotion();
    };
  }, [rootRef]);
}
